import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    outstandingDocuments: [],

    allocations: [],

    selectedPayment: null,

    outstandingPagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },

    allocationPagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },

    allocationSummary: {
        totalAllocated: 0,
        totalReversed: 0,
        totalActive: 0,
        allocationCount: 0,
    },

    loadingOutstanding: false,
    loadingAllocations: false,
    allocating: false,
    reversing: false,

    error: "",
    successMessage: "",
};

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsedValue =
        Number(value);

    return Number.isFinite(
        parsedValue
    )
        ? parsedValue
        : fallback;
};

const cleanQueryParams = (
    params = {}
) => {
    const cleaned =
        {};

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value ===
                undefined ||
                value ===
                null ||
                value ===
                ""
            ) {
                return;
            }

            cleaned[key] =
                value;
        }
    );

    return cleaned;
};

const createQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        cleanQueryParams(
            params
        )
    ).forEach(
        ([
            key,
            value,
        ]) => {
            query.set(
                key,
                String(value)
            );
        }
    );

    const queryString =
        query.toString();

    return queryString
        ? `?${queryString}`
        : "";
};

const getEnvelope = (
    response
) => {
    if (
        response?.data &&
        typeof response.data ===
        "object" &&
        (
            response.data.success !==
            undefined ||
            response.data.status ||
            response.data.code
        )
    ) {
        return response.data;
    }

    return response || {};
};

const getPayloadData = (
    response
) => {
    const envelope =
        getEnvelope(
            response
        );

    return (
        envelope?.data ||
        {}
    );
};

const getPayloadMeta = (
    response
) => {
    const envelope =
        getEnvelope(
            response
        );

    return (
        envelope?.meta ||
        envelope?.data?.pagination ||
        {}
    );
};

const getErrorMessage = (
    error,
    fallback
) => {
    return (
        error?.response?.data
            ?.message ||
        error?.data?.message ||
        error?.payload?.message ||
        (
            typeof error?.payload ===
                "string"
                ? error.payload
                : ""
        ) ||
        error?.message ||
        fallback
    );
};

const normalizePagination = (
    pagination = {}
) => {
    const page =
        Math.max(
            toNumber(
                pagination?.page,
                1
            ),
            1
        );

    const limit =
        Math.max(
            toNumber(
                pagination?.limit,
                50
            ),
            1
        );

    const total =
        Math.max(
            toNumber(
                pagination?.total,
                0
            ),
            0
        );

    const totalPages =
        Math.max(
            toNumber(
                pagination?.totalPages,
                total > 0
                    ? Math.ceil(
                        total /
                        limit
                    )
                    : 1
            ),
            1
        );

    return {
        page,
        limit,
        total,
        totalPages,

        hasNextPage:
            typeof pagination
                ?.hasNextPage ===
                "boolean"
                ? pagination
                    .hasNextPage
                : page <
                totalPages,

        hasPreviousPage:
            typeof pagination
                ?.hasPreviousPage ===
                "boolean"
                ? pagination
                    .hasPreviousPage
                : page >
                1,
    };
};

const normalizeOutstandingDocument = (
    document = {}
) => {
    return {
        ...document,

        grandTotal:
            toNumber(
                document?.grandTotal ??
                document?.totals
                    ?.grandTotal
            ),

        paidAmount:
            toNumber(
                document?.paidAmount ??
                document?.totals
                    ?.paidAmount
            ),

        balanceAmount:
            toNumber(
                document
                    ?.balanceAmount ??
                document?.totals
                    ?.balanceAmount
            ),
    };
};

const normalizeAllocation = (
    allocation = {}
) => {
    return {
        ...allocation,

        allocatedAmount:
            toNumber(
                allocation
                    ?.allocatedAmount
            ),

        reversedAmount:
            toNumber(
                allocation
                    ?.reversedAmount
            ),

        activeAmount:
            toNumber(
                allocation
                    ?.activeAmount
            ),

        payment: {
            ...(
                allocation?.payment ||
                {}
            ),

            amount:
                toNumber(
                    allocation
                        ?.payment
                        ?.amount
                ),

            allocatedAmount:
                toNumber(
                    allocation
                        ?.payment
                        ?.allocatedAmount
                ),

            unallocatedAmount:
                toNumber(
                    allocation
                        ?.payment
                        ?.unallocatedAmount
                ),
        },
    };
};

/*
|--------------------------------------------------------------------------
| FETCH OUTSTANDING INVOICES
|--------------------------------------------------------------------------
*/

export const fetchCustomerOutstandingInvoices =
    createAsyncThunk(
        "paymentAllocation/fetchOutstandingInvoices",

        async (
            {
                customerId,
                page = 1,
                limit = 50,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (!customerId) {
                    return rejectWithValue(
                        "Customer ID is required."
                    );
                }

                if (
                    typeof config
                        ?.getCustomerOutstandingInvoices !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Outstanding invoices API URL is not configured."
                    );
                }

                const queryString =
                    createQueryString({
                        page,
                        limit,
                    });

                const api =
                    new API();

                const response =
                    await api.get(
                        `${config.getCustomerOutstandingInvoices(
                            customerId
                        )}${queryString}`
                    );

                const envelope =
                    getEnvelope(
                        response
                    );

                if (
                    envelope?.success ===
                    false ||
                    envelope?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        envelope?.message ||
                        "Unable to fetch outstanding invoices."
                    );
                }

                const data =
                    getPayloadData(
                        response
                    );

                const documents =
                    Array.isArray(
                        data?.documents
                    )
                        ? data.documents.map(
                            normalizeOutstandingDocument
                        )
                        : [];

                return {
                    documents,

                    pagination:
                        normalizePagination(
                            getPayloadMeta(
                                response
                            )
                        ),
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch outstanding invoices."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| ALLOCATE EXISTING PAYMENT
|--------------------------------------------------------------------------
*/

export const allocateExistingPayment =
    createAsyncThunk(
        "paymentAllocation/allocateExistingPayment",

        async (
            {
                paymentId,
                allocations = [],
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (!paymentId) {
                    return rejectWithValue(
                        "Payment ID is required."
                    );
                }

                if (
                    !Array.isArray(
                        allocations
                    ) ||
                    allocations.length ===
                    0
                ) {
                    return rejectWithValue(
                        "At least one allocation is required."
                    );
                }

                if (
                    typeof config
                        ?.allocateExistingPayment !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Payment allocation API URL is not configured."
                    );
                }

                const api =
                    new API();

                const response =
                    await api.post(
                        config.allocateExistingPayment(
                            paymentId
                        ),
                        {
                            allocations,
                        }
                    );

                const envelope =
                    getEnvelope(
                        response
                    );

                if (
                    envelope?.success ===
                    false ||
                    envelope?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        envelope?.message ||
                        "Unable to allocate payment."
                    );
                }

                const data =
                    getPayloadData(
                        response
                    );

                return {
                    payment:
                        data?.payment ||
                        null,

                    allocations:
                        Array.isArray(
                            data?.allocations
                        )
                            ? data.allocations.map(
                                normalizeAllocation
                            )
                            : [],

                    message:
                        envelope?.message ||
                        "Payment allocated successfully.",
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to allocate payment."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH PAYMENT ALLOCATIONS
|--------------------------------------------------------------------------
*/

export const fetchPaymentAllocations =
    createAsyncThunk(
        "paymentAllocation/fetchPaymentAllocations",

        async (
            {
                paymentId,
                status = "",
                page = 1,
                limit = 50,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (!paymentId) {
                    return rejectWithValue(
                        "Payment ID is required."
                    );
                }

                if (
                    typeof config
                        ?.getPaymentAllocations !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Payment allocations API URL is not configured."
                    );
                }

                const queryString =
                    createQueryString({
                        status,
                        page,
                        limit,
                    });

                const api =
                    new API();

                const response =
                    await api.get(
                        `${config.getPaymentAllocations(
                            paymentId
                        )}${queryString}`
                    );

                const envelope =
                    getEnvelope(
                        response
                    );

                if (
                    envelope?.success ===
                    false ||
                    envelope?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        envelope?.message ||
                        "Unable to fetch payment allocations."
                    );
                }

                const data =
                    getPayloadData(
                        response
                    );

                return {
                    allocations:
                        Array.isArray(
                            data?.allocations
                        )
                            ? data.allocations.map(
                                normalizeAllocation
                            )
                            : [],

                    summary: {
                        totalAllocated:
                            toNumber(
                                data?.summary
                                    ?.totalAllocated
                            ),

                        totalReversed:
                            toNumber(
                                data?.summary
                                    ?.totalReversed
                            ),

                        totalActive:
                            toNumber(
                                data?.summary
                                    ?.totalActive
                            ),

                        allocationCount:
                            toNumber(
                                data?.summary
                                    ?.allocationCount
                            ),
                    },

                    pagination:
                        normalizePagination(
                            getPayloadMeta(
                                response
                            )
                        ),
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch payment allocations."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| REVERSE PAYMENT ALLOCATION
|--------------------------------------------------------------------------
*/

export const reversePaymentAllocation =
    createAsyncThunk(
        "paymentAllocation/reversePaymentAllocation",

        async (
            {
                allocationId,
                amount = null,
                reason,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (!allocationId) {
                    return rejectWithValue(
                        "Allocation ID is required."
                    );
                }

                if (!reason?.trim()) {
                    return rejectWithValue(
                        "Reversal reason is required."
                    );
                }

                if (
                    typeof config
                        ?.reversePaymentAllocation !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Reverse allocation API URL is not configured."
                    );
                }

                const payload = {
                    reason:
                        reason.trim(),
                };

                if (
                    amount !== null &&
                    amount !== undefined &&
                    amount !== ""
                ) {
                    payload.amount =
                        Number(amount);
                }

                const api =
                    new API();

                const response =
                    await api.post(
                        config.reversePaymentAllocation(
                            allocationId
                        ),
                        payload
                    );

                const envelope =
                    getEnvelope(
                        response
                    );

                if (
                    envelope?.success ===
                    false ||
                    envelope?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        envelope?.message ||
                        "Unable to reverse payment allocation."
                    );
                }

                const data =
                    getPayloadData(
                        response
                    );

                return {
                    allocation:
                        normalizeAllocation(
                            data?.allocation ||
                            {}
                        ),

                    message:
                        envelope?.message ||
                        "Payment allocation reversed successfully.",
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to reverse payment allocation."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const paymentAllocationSlice =
    createSlice({
        name:
            "paymentAllocation",

        initialState,

        reducers: {
            clearPaymentAllocationError: (
                state
            ) => {
                state.error =
                    "";
            },

            clearPaymentAllocationSuccess: (
                state
            ) => {
                state.successMessage =
                    "";
            },

            clearOutstandingDocuments: (
                state
            ) => {
                state.outstandingDocuments =
                    [];

                state.outstandingPagination = {
                    ...initialState
                        .outstandingPagination,
                };
            },

            clearPaymentAllocations: (
                state
            ) => {
                state.allocations =
                    [];

                state.allocationSummary = {
                    ...initialState
                        .allocationSummary,
                };

                state.allocationPagination = {
                    ...initialState
                        .allocationPagination,
                };
            },

            setSelectedAllocationPayment: (
                state,
                action
            ) => {
                state.selectedPayment =
                    action.payload ||
                    null;
            },

            resetPaymentAllocationState:
                () => ({
                    ...initialState,

                    outstandingDocuments:
                        [],

                    allocations:
                        [],

                    outstandingPagination: {
                        ...initialState
                            .outstandingPagination,
                    },

                    allocationPagination: {
                        ...initialState
                            .allocationPagination,
                    },

                    allocationSummary: {
                        ...initialState
                            .allocationSummary,
                    },
                }),
        },

        extraReducers: (
            builder
        ) => {
            builder

                /*
                |--------------------------------------------------------------------------
                | OUTSTANDING INVOICES
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchCustomerOutstandingInvoices
                        .pending,
                    (
                        state
                    ) => {
                        state.loadingOutstanding =
                            true;

                        state.error =
                            "";
                    }
                )

                .addCase(
                    fetchCustomerOutstandingInvoices
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loadingOutstanding =
                            false;

                        state.outstandingDocuments =
                            Array.isArray(
                                action.payload
                                    ?.documents
                            )
                                ? action.payload
                                    .documents
                                : [];

                        state.outstandingPagination = {
                            ...initialState
                                .outstandingPagination,

                            ...(
                                action.payload
                                    ?.pagination ||
                                {}
                            ),
                        };
                    }
                )

                .addCase(
                    fetchCustomerOutstandingInvoices
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loadingOutstanding =
                            false;

                        state.error =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to fetch outstanding invoices.";
                    }
                )

                /*
                |--------------------------------------------------------------------------
                | ALLOCATE PAYMENT
                |--------------------------------------------------------------------------
                */

                .addCase(
                    allocateExistingPayment
                        .pending,
                    (
                        state
                    ) => {
                        state.allocating =
                            true;

                        state.error =
                            "";

                        state.successMessage =
                            "";
                    }
                )

                .addCase(
                    allocateExistingPayment
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.allocating =
                            false;

                        state.selectedPayment =
                            action.payload
                                ?.payment ||
                            state.selectedPayment;

                        const newAllocations =
                            Array.isArray(
                                action.payload
                                    ?.allocations
                            )
                                ? action.payload
                                    .allocations
                                : [];

                        newAllocations.forEach(
                            (
                                allocation
                            ) => {
                                const existingIndex =
                                    state.allocations.findIndex(
                                        (
                                            item
                                        ) =>
                                            item?._id ===
                                            allocation
                                                ?._id
                                    );

                                if (
                                    existingIndex >=
                                    0
                                ) {
                                    state.allocations[
                                        existingIndex
                                    ] =
                                        allocation;
                                } else {
                                    state.allocations.unshift(
                                        allocation
                                    );
                                }
                            }
                        );

                        state.successMessage =
                            action.payload
                                ?.message ||
                            "Payment allocated successfully.";
                    }
                )

                .addCase(
                    allocateExistingPayment
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.allocating =
                            false;

                        state.error =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to allocate payment.";
                    }
                )

                /*
                |--------------------------------------------------------------------------
                | FETCH ALLOCATIONS
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchPaymentAllocations
                        .pending,
                    (
                        state
                    ) => {
                        state.loadingAllocations =
                            true;

                        state.error =
                            "";
                    }
                )

                .addCase(
                    fetchPaymentAllocations
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loadingAllocations =
                            false;

                        state.allocations =
                            Array.isArray(
                                action.payload
                                    ?.allocations
                            )
                                ? action.payload
                                    .allocations
                                : [];

                        state.allocationSummary = {
                            ...initialState
                                .allocationSummary,

                            ...(
                                action.payload
                                    ?.summary ||
                                {}
                            ),
                        };

                        state.allocationPagination = {
                            ...initialState
                                .allocationPagination,

                            ...(
                                action.payload
                                    ?.pagination ||
                                {}
                            ),
                        };
                    }
                )

                .addCase(
                    fetchPaymentAllocations
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loadingAllocations =
                            false;

                        state.error =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to fetch payment allocations.";
                    }
                )

                /*
                |--------------------------------------------------------------------------
                | REVERSE ALLOCATION
                |--------------------------------------------------------------------------
                */

                .addCase(
                    reversePaymentAllocation
                        .pending,
                    (
                        state
                    ) => {
                        state.reversing =
                            true;

                        state.error =
                            "";

                        state.successMessage =
                            "";
                    }
                )

                .addCase(
                    reversePaymentAllocation
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.reversing =
                            false;

                        const allocation =
                            action.payload
                                ?.allocation;

                        if (
                            allocation
                                ?._id
                        ) {
                            const existingIndex =
                                state.allocations.findIndex(
                                    (
                                        item
                                    ) =>
                                        item?._id ===
                                        allocation._id
                                );

                            if (
                                existingIndex >=
                                0
                            ) {
                                state.allocations[
                                    existingIndex
                                ] =
                                    allocation;
                            }
                        }

                        state.successMessage =
                            action.payload
                                ?.message ||
                            "Payment allocation reversed successfully.";
                    }
                )

                .addCase(
                    reversePaymentAllocation
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.reversing =
                            false;

                        state.error =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to reverse payment allocation.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearPaymentAllocationError,
    clearPaymentAllocationSuccess,
    clearOutstandingDocuments,
    clearPaymentAllocations,
    setSelectedAllocationPayment,
    resetPaymentAllocationState,
} =
    paymentAllocationSlice.actions;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const paymentAllocationSelector =
    (
        state
    ) =>
        state
            ?.paymentAllocation ||
        state
            ?.billing
            ?.paymentAllocation ||
        initialState;

export const outstandingDocumentsSelector =
    (
        state
    ) =>
        paymentAllocationSelector(
            state
        ).outstandingDocuments;

export const paymentAllocationsSelector =
    (
        state
    ) =>
        paymentAllocationSelector(
            state
        ).allocations;

export const paymentAllocationSummarySelector =
    (
        state
    ) =>
        paymentAllocationSelector(
            state
        ).allocationSummary;

export const selectedAllocationPaymentSelector =
    (
        state
    ) =>
        paymentAllocationSelector(
            state
        ).selectedPayment;

export const outstandingPaginationSelector =
    (
        state
    ) =>
        paymentAllocationSelector(
            state
        ).outstandingPagination;

export const allocationPaginationSelector =
    (
        state
    ) =>
        paymentAllocationSelector(
            state
        ).allocationPagination;

export const loadingOutstandingSelector =
    (
        state
    ) =>
        Boolean(
            paymentAllocationSelector(
                state
            ).loadingOutstanding
        );

export const loadingAllocationsSelector =
    (
        state
    ) =>
        Boolean(
            paymentAllocationSelector(
                state
            ).loadingAllocations
        );

export const allocatingPaymentSelector =
    (
        state
    ) =>
        Boolean(
            paymentAllocationSelector(
                state
            ).allocating
        );

export const reversingAllocationSelector =
    (
        state
    ) =>
        Boolean(
            paymentAllocationSelector(
                state
            ).reversing
        );

export const paymentAllocationErrorSelector =
    (
        state
    ) =>
        paymentAllocationSelector(
            state
        ).error;

export const paymentAllocationSuccessSelector =
    (
        state
    ) =>
        paymentAllocationSelector(
            state
        ).successMessage;

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default
    paymentAllocationSlice.reducer;